import { useGetFrontAndBackDots } from 'app/my-skin/components/BodySymtomsLocation/hooks';
import { PreworkItemContainer } from './PreworkItemContainer';
import { PreworkSymptomsLocationSection } from './PreworkSymptomsLocationSection';
import { useMemo } from 'react';
import { GetAppointmentMediaOutput } from 'graphql/generated/hasura';
import { DotBodyGroup } from '../../my-skin/components/BodySymtomsLocation/interfaces';

interface PreworkSymptomsLocationProps {
  question: string;
  frontLabel: string;
  backLabel: string;
  headLabel: string;
  codexPatientId: string;
  symptomsLocation: GetAppointmentMediaOutput[] | null | undefined;
}

export const PreworkSymptomsLocation: React.FC<
  PreworkSymptomsLocationProps
> = ({
  question,
  frontLabel,
  backLabel,
  headLabel,
  symptomsLocation,
  codexPatientId,
}) => {
  const { backDots: allBackDots, frontDots: allFrontDots } =
    useGetFrontAndBackDots();

  const headDots = useMemo(() => {
    if (
      !symptomsLocation?.length ||
      !allFrontDots?.length ||
      !allBackDots?.length
    )
      return [];
    const dotsLocationSet = new Set(
      symptomsLocation.map(({ bodySite }) => bodySite),
    );
    return [...allBackDots, ...allFrontDots]
      .filter(
        (dot) =>
          dot.location &&
          dot.group === DotBodyGroup.HEAD &&
          dotsLocationSet.has(dot.location),
      )
      .map((dot) => dot.location as string);
  }, [allBackDots, allFrontDots, symptomsLocation]);

  const frontDotsLocations = useMemo<string[]>(() => {
    if (!symptomsLocation?.length || !allFrontDots?.length) return [];
    const frontDotsLocationSet = new Set(
      symptomsLocation.map(({ bodySite }) => bodySite),
    );
    return allFrontDots
      .filter(
        (dot) =>
          dot.location &&
          dot.group !== DotBodyGroup.HEAD &&
          frontDotsLocationSet.has(dot.location),
      )
      .map((dot) => dot.location as string);
  }, [symptomsLocation, allFrontDots]);

  const backDotsLocations = useMemo<string[]>(() => {
    if (!symptomsLocation?.length || !allBackDots?.length) return [];
    const backDotsLocationSet = new Set(
      symptomsLocation.map(({ bodySite }) => bodySite),
    );
    return allBackDots
      .filter(
        (dot) =>
          dot.location &&
          dot.group !== DotBodyGroup.HEAD &&
          backDotsLocationSet.has(dot.location),
      )
      .map((dot) => dot.location as string);
  }, [symptomsLocation, allBackDots]);

  return (
    <PreworkItemContainer question={question} gap="gap-3">
      <div className="flex flex-col gap-2.5 overflow-y-auto max-h-[320px] scrollbar-thin scrollbar-thumb-rounded-full scrollbar-track-rounded-full">
        {!!headDots.length && (
          <PreworkSymptomsLocationSection
            title={headLabel}
            codexPatientId={codexPatientId}
            symptoms={headDots}
            symptomsMedia={symptomsLocation}
          />
        )}
        {!!frontDotsLocations.length && (
          <PreworkSymptomsLocationSection
            title={frontLabel}
            codexPatientId={codexPatientId}
            symptoms={frontDotsLocations}
            symptomsMedia={symptomsLocation}
          />
        )}
        {!!backDotsLocations.length && (
          <PreworkSymptomsLocationSection
            title={backLabel}
            symptoms={backDotsLocations}
            codexPatientId={codexPatientId}
            symptomsMedia={symptomsLocation}
          />
        )}
      </div>
    </PreworkItemContainer>
  );
};
